import React, { Component, Fragment } from 'react';
import { arrayOf, string, objectOf, object, array } from 'prop-types';
import styled from 'styled-components';
import * as d3 from 'd3';
import { debounce } from 'lodash';

import { withGroupContext } from 'context/groupContext';

import Circle from './Circle/Circle';
import CirclePackingTooltip from './CirclePackingTooltip/CirclePackingTooltip';

import mapData from './mapData';

const ChartContainer = styled('div')`
  flex: 1 1 auto;
  min-width: 0%;
  height: 100%;
`;

class CirclePackingChart extends Component {
  constructor(props) {
    super(props);

    const margin = {
      top: 5,
      right: 5,
      bottom: 5,
      left: 5,
    };
    const aspectRatio = 1;
    const size = {
      width: 600 - margin.left - margin.right,
      maxHeight: 700 - margin.top - margin.bottom,
    };
    const padding = 10;

    this.chartContainer = React.createRef();
    this.debouncedSetSizes = debounce(this.setSizes, 200);

    this.state = {
      size,
      aspectRatio,
      margin,
      padding,
      hoveredNode: null,
    };
  }

  generateNodes = (groupIds, scores, trendScores) => {
    const sumScores = mapData(groupIds, scores, trendScores);
    const layout = d3
      .pack()
      .size([this.state.size.width, this.state.size.height])
      .padding(this.state.padding);
    const root = d3.hierarchy(sumScores).sum(d => d.data.value);
    layout(root);

    return root.descendants();
  };

  setSizes = () => {
    const { aspectRatio, size, margin } = this.state;

    const newWidth =
      this.chartContainer.current &&
      this.chartContainer.current.getBoundingClientRect().width;

    if (!newWidth || newWidth === size.width) {
      return;
    }

    /**
     * Set height based on the aspect ratio and prevent it from being
     * bigger than the maxHeight.
     */
    const newHeight = Math.floor(
      newWidth * aspectRatio - margin.top - margin.bottom
    );
    const newSize = {
      width: Math.floor(newWidth - margin.left - margin.right),
      height: newHeight > size.maxHeight ? size.maxHeight : newHeight,
    };

    this.setState({
      size: {
        ...size,
        width: newSize.width,
        height: newSize.height,
      },
    });
  };

  componentDidMount() {
    // Listen for resize events to resize
    window.addEventListener('resize', this.debouncedSetSizes);
    this.setSizes();
  }

  componentWillUnmount() {
    // Remove resize event listener
    window.removeEventListener('resize', this.debouncedSetSizes);
  }

  handleHover = node => {
    this.setState({ hoveredNode: node });
  };

  handleMouseLeave = () => {
    this.setState({ hoveredNode: null });
  };

  render() {
    const { scores, trendScores, groupsWithData, colorById } = this.props;
    const { size, margin, hoveredNode } = this.state;
    const nodes = this.generateNodes(groupsWithData, scores, trendScores, size);

    const filteredNodes = nodes.filter(
      ({ data: { id } }) => id !== 'container'
    );

    return (
      <ChartContainer innerRef={this.chartContainer} id="attitude-heatmap">
        <Fragment>
          {nodes &&
            size.width &&
            size.height && (
              <svg
                width={size.width + margin.left + margin.right}
                height={size.height + margin.top + margin.bottom}
              >
                <g
                  transform={`translate(${margin.left},${margin.top})`}
                  onMouseLeave={this.handleMouseLeave}
                >
                  {filteredNodes.map((node, index) => (
                    <Circle
                      key={node.data.id}
                      node={node}
                      index={index}
                      color={colorById[node.data.id]}
                      handleHover={this.handleHover}
                    />
                  ))}
                </g>
              </svg>
            )}
          {hoveredNode && (
            <CirclePackingTooltip node={hoveredNode} margin={margin} />
          )}
        </Fragment>
      </ChartContainer>
    );
  }
}

CirclePackingChart.propTypes = {
  groupsWithData: arrayOf(string).isRequired,
  scores: objectOf(array),
  trendScores: objectOf(object).isRequired,
  colorById: objectOf(string).isRequired,
};

export default withGroupContext(CirclePackingChart);



// WEBPACK FOOTER //
// ./src/components/CirclePackingChart/CirclePackingChart.js